import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MapPin, Edit, Trash2, Plus, Search, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { customersAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import { phoneMatchesSearch } from '../../utils/customerSearch';

interface Customer {
  _id: string;
  firstName: string;
  lastName?: string;
  nickname?: string;
  email?: string;
  phone?: string;
  address?: string;
  postcode?: string;
  city?: string;
  isBusiness?: boolean;
  notes?: string;
  gdprConsent?: boolean;
  marketingConsent?: boolean;
  preferredNotificationMethod?: 'email' | 'sms' | 'both';
}

const Customers: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  React.useEffect(() => {
    setCurrentPage(1); // Reset page on search
  }, [searchTerm]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    city: '',
    isBusiness: false,
    notes: '',
    gdprConsent: false,
    marketingConsent: false,
    preferredNotificationMethod: 'email',
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errors.email = 'Enter a valid email address';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getCustomers();
      setCustomers(response.data);
    } catch (err: any) {
      setError(err);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const createCustomer = async () => {
    if (!validateForm()) return;
    try {
      const response = await customersAPI.createCustomer(formData as any);
      setCustomers([response.data, ...customers]);
      setShowAddModal(false);
      setEditingCustomer(null);
      setFormData({
        firstName: '',
        lastName: '',
        nickname: '',
        email: '',
        phone: '',
        address: '',
        postcode: '',
        city: '',
        isBusiness: false,
        notes: '',
        gdprConsent: false,
        marketingConsent: false,
        preferredNotificationMethod: 'email',
      });
      toast.success('Customer created successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create customer');
    }
  };

  const updateCustomer = async () => {
    if (!editingCustomer) return;
    if (!validateForm()) return;
    try {
      const response = await customersAPI.updateCustomer(editingCustomer._id, formData);
      setCustomers(customers.map(c => c._id === editingCustomer._id ? response.data : c));
      setEditingCustomer(null);
      setShowAddModal(false);
      toast.success('Customer updated successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update customer');
    }
  };

  const deleteCustomer = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await customersAPI.deleteCustomer(id);
      setCustomers(customers.filter(c => c._id !== id));
      toast.success('Customer deleted successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete customer');
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      firstName: customer.firstName,
      lastName: customer.lastName || '',
      nickname: customer.nickname || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      postcode: customer.postcode || '',
      city: customer.city || '',
      isBusiness: customer.isBusiness || false,
      notes: customer.notes || '',
      gdprConsent: customer.gdprConsent || false,
      marketingConsent: customer.marketingConsent || false,
      preferredNotificationMethod: customer.preferredNotificationMethod || 'email',
    });
    setFormErrors({}); // Clear errors when opening for edit
    setShowAddModal(true);
  };

  const filteredCustomers = customers?.filter((customer: Customer) =>
    customer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.nickname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    phoneMatchesSearch(customer.phone, searchTerm)
  ) || [];

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  React.useEffect(() => {
    fetchCustomers();
  }, []);

  if (!user) {
    return <div>Please log in to view customers.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="sm:flex-auto flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <img src="/hmpos_logo.jpeg" alt="Logo" className="h-10 w-auto rounded" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
              <p className="mt-2 text-sm text-gray-700">Manage your customer database</p>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
            <button
              onClick={() => {
                setShowAddModal(true);
                setEditingCustomer(null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  nickname: '',
                  email: '',
                  phone: '',
                  address: '',
                  postcode: '',
                  city: '',
                  isBusiness: false,
                  notes: '',
                  gdprConsent: false,
                  marketingConsent: false,
                  preferredNotificationMethod: 'email',
                });
                setFormErrors({});
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Search name, nickname, phone or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Customer List */}
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
          {error && (
            <div className="p-4 text-red-700 bg-red-50 border border-red-200 rounded-md">
              Error loading customers: {error.message || 'Unknown error'}
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {paginatedCustomers.map((customer) => (
                <li key={customer._id} className="p-6 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/customers/${customer._id}`)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-lg">
                            {customer.firstName.charAt(0)}{customer.lastName?.charAt(0) || ''}
                          </span>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            {customer.firstName} {customer.lastName}{customer.nickname ? ` (${customer.nickname})` : ''}
                          </p>
                          <p className="text-sm text-gray-500">
                            {customer.email}
                          </p>
                          {customer.phone && (
                            <p className="text-sm text-gray-500 flex items-center">
                              <Phone className="h-4 w-4 mr-1" />
                              {customer.phone}
                            </p>
                          )}
                          {customer.address && (
                            <p className="text-sm text-gray-500 flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {customer.address}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent navigating to customer details
                          handleEditCustomer(customer);
                        }}
                        className="p-1 text-gray-400 hover:text-primary-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent navigating to customer details
                          deleteCustomer(customer._id);
                        }}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Pagination Controls */}
          {!loading && filteredCustomers.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredCustomers.length)}</span> of <span className="font-medium">{filteredCustomers.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="sr-only">Previous</span>
                      <span aria-hidden="true">&larr;</span>
                    </button>
                    {/* Page Numbers */}
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="sr-only">Next</span>
                      <span aria-hidden="true">&rarr;</span>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Add/Edit Modal */}
        {(showAddModal) && (
          <div className="fixed inset-0 overflow-y-auto z-50 bg-gray-500 bg-opacity-75 transition-opacity">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* This is for centering the modal */}
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="sm:flex-auto">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                      </h3>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-4 sm:ml-0 sm:flex-none">
                      <button
                        onClick={() => {
                          setShowAddModal(false);
                          setEditingCustomer(null);
                          setFormErrors({}); // Clear errors on cancel
                        }}
                        className="rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  <form className="mt-5 space-y-4" onSubmit={(e) => {
                    e.preventDefault();
                    editingCustomer ? updateCustomer() : createCustomer();
                  }}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">First Name</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        />
                        {formErrors.firstName && <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Last Name</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        />
                        {formErrors.lastName && <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nickname (internal only)</label>
                        <input
                          type="text"
                          maxLength={80}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.nickname}
                          onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                        {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <input
                          type="tel"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Postcode</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.postcode}
                          onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">City</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <textarea
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <textarea
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                          checked={formData.isBusiness}
                          onChange={(e) => setFormData({ ...formData, isBusiness: e.target.checked })}
                        />
                        Is Business Customer
                      </label>
                    </div>

                    <div className="grid grid-cols-1 gap-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Preferences & Consents</label>
                        <div className="space-y-3">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                              checked={formData.gdprConsent}
                              onChange={(e) => setFormData({ ...formData, gdprConsent: e.target.checked })}
                            />
                            GDPR Consent
                          </label>
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                              checked={formData.marketingConsent}
                              onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                            />
                            Marketing Consent
                          </label>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Notification Method</label>
                            <select
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md shadow-sm"
                              value={formData.preferredNotificationMethod}
                              onChange={(e) => setFormData({ ...formData, preferredNotificationMethod: e.target.value as any })}
                            >
                              <option value="email">Email</option>
                              <option value="sms">SMS</option>
                              <option value="both">Both</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {editingCustomer ? 'Update Customer' : 'Create Customer'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
